import React, { useEffect, useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SEASON_THEMES } from "@/lib/seasons";

const KEY = "hb_season_reminders";
const DONE_KEY = "hb_season_emails";

export default function ComingSoonTeaser({ days = 45 }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seasons/upcoming?days=${days}`)
      .then((r) => r.json()).then((d) => { if (d.upcoming) { setData(d); setDone((JSON.parse(localStorage.getItem(DONE_KEY) || "[]")).includes(d.slug)); } })
      .catch(() => {});
  }, [days]);

  if (!data) return null;
  const t = SEASON_THEMES[data.slug] || SEASON_THEMES.default;
  const when = new Date(data.starts_on).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  const dayBefore = new Date(new Date(data.starts_on).getTime() - 86400000).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/seasons/notify", { email: email.trim(), slug: data.slug, join_pack: true });
      const list = JSON.parse(localStorage.getItem(KEY) || "[]");
      if (!list.includes(data.slug)) localStorage.setItem(KEY, JSON.stringify([...list, data.slug]));
      const doneList = JSON.parse(localStorage.getItem(DONE_KEY) || "[]");
      localStorage.setItem(DONE_KEY, JSON.stringify([...doneList, data.slug]));
      setDone(true);
      toast.success(`You're in! Your early-access link arrives ${dayBefore} — a day before ${data.label} opens ${data.emoji}`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "That email doesn't look right.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl p-5 sm:p-6 mb-6 flex flex-col lg:flex-row lg:items-center gap-4 soft-shadow relative overflow-hidden"
      style={{ background: t.bg, color: t.fg }} data-testid="coming-soon-teaser">
      <div className="flex -space-x-3 shrink-0" data-testid="coming-soon-images">
        {data.images.slice(0, 3).map((src, i) => (
          <img key={i} src={src} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-white/80 bg-white" style={{ filter: "saturate(0.6)" }} />
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: t.fgSoft }}>Coming soon</p>
        <h3 className="font-display font-extrabold text-xl sm:text-2xl leading-tight" style={{ color: t.fg }} data-testid="coming-soon-title">
          {data.emoji} {data.label} collection lands {when}
        </h3>
        <p className="text-sm mt-1" style={{ color: t.fgSoft }} data-testid="coming-soon-sub">
          {data.product_count} {data.product_count === 1 ? "piece is" : "pieces are"} warming up backstage — {data.days_until === 0 ? "opening today" : `${data.days_until} day${data.days_until === 1 ? "" : "s"} to go`}.
        </p>
        <p className="text-xs mt-1.5 font-semibold" style={{ color: t.fgSoft }} data-testid="coming-soon-early-note">
          ✨ Pack members shop it 24 hours early — pop your email in and we'll send your private early-access link on {dayBefore}.
        </p>
      </div>
      {done ? (
        <p className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display font-bold text-sm" style={{ background: t.pill, color: t.pillFg }} data-testid="coming-soon-done">
          <Check className="w-4 h-4" /> You're in — early-access link lands {dayBefore}
        </p>
      ) : (
        <div className="shrink-0 w-full lg:w-auto">
        <form onSubmit={submit} className="flex items-center gap-2 bg-white/95 rounded-full p-1 pl-4 w-full lg:w-auto" data-testid="coming-soon-form">
          <Mail className="w-4 h-4 text-[#19324A]/50 shrink-0" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
            className="min-w-0 flex-1 lg:w-48 bg-transparent text-sm text-[#19324A] outline-none py-2" data-testid="coming-soon-email" />
          <button type="submit" disabled={busy} data-testid="coming-soon-submit"
            className="shrink-0 rounded-full px-4 py-2 font-display font-bold text-sm transition-transform hover:scale-105 disabled:opacity-60"
            style={{ background: t.cta, color: t.ctaFg }}>
            {busy ? "Saving…" : "Get early access"}
          </button>
        </form>
        <p className="text-[10px] mt-1.5 text-center lg:text-right" style={{ color: t.fgSoft }} data-testid="coming-soon-consent">Joins The Happy Bulldog Pack newsletter · unsubscribe anytime</p>
        </div>
      )}
    </div>
  );
}
